import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ProductoApiService } from '../services/producto-api.service';
import { ProductoPriceCurrencyApiService } from '../services/producto-price-currency-api.service';
import { CurrencyResponse } from '../models/producto.model';
import {
  ProductPriceCurrencyResponse,
  ProductPriceCurrencyRequest
} from '../models/producto-price-currency.model';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { formatMoney } from '../../../shared/utils/currency-format.utils';

@Component({
  selector: 'app-producto-price-currencies',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './producto-price-currencies.component.html'
})
export class ProductoPriceCurrenciesComponent implements OnInit {
  @Input() productId!: number;
  @Input() businessId!: number;

  priceCurrencies: ProductPriceCurrencyResponse[] = [];
  currencies: CurrencyResponse[] = [];
  loading = false;
  error = '';

  // Form
  showForm = false;
  formCurrencyId: number | null = null;
  formPriceType = 'fixed';
  formFixedPrice: number | null = null;
  saving = false;

  // Resolver — el backend devuelve un número plano (Mono<BigDecimal>), no un objeto (Incidente #37).
  resolveCurrencyId: number | null = null;
  resolveResult: number | null = null;
  resolveError: string | null = null;
  resolving = false;

  constructor(
    private productApi: ProductoApiService,
    private priceCurrencyApi: ProductoPriceCurrencyApiService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargarDatos();
  }

  cargarDatos(): void {
    this.loading = true;
    this.productApi.getCurrencies().subscribe({
      next: (data) => { this.currencies = data; },
      error: () => {}
    });
    this.priceCurrencyApi.getProductPriceCurrencies(this.productId).subscribe({
      next: (data) => {
        this.priceCurrencies = data;
        this.loading = false;
      },
      error: () => {
        this.priceCurrencies = [];
        this.loading = false;
      }
    });
  }

  getCurrencyName(currencyId: number): string {
    const c = this.currencies.find(c => c.id === currencyId);
    return c ? `${c.name} (${c.code})` : `#${currencyId}`;
  }

  abrirForm(): void {
    this.formCurrencyId = null;
    this.formPriceType = 'fixed';
    this.formFixedPrice = null;
    this.showForm = true;
  }

  cerrarForm(): void {
    this.showForm = false;
  }

  guardar(): void {
    if (!this.formCurrencyId) return;
    this.saving = true;
    this.error = '';

    const req: ProductPriceCurrencyRequest = {
      currencyId: this.formCurrencyId,
      priceType: this.formPriceType,
      fixedPrice: this.formPriceType === 'fixed' ? (this.formFixedPrice ?? undefined) : undefined
    };

    this.priceCurrencyApi.upsertProductPriceCurrency(this.productId, req, this.businessId).subscribe({
      next: () => {
        this.saving = false;
        this.cerrarForm();
        this.cargarDatos();
      },
      error: (err) => {
        this.saving = false;
        this.error = err.error?.message || 'Error al guardar el precio';
      }
    });
  }

  async eliminar(currencyId: number): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar precio',
      message: '¿Eliminar el precio para esta moneda?'
    });
    if (!ok) return;
    this.priceCurrencyApi.deleteProductPriceCurrency(this.productId, currencyId, this.businessId).subscribe({
      next: () => {
        this.cargarDatos();
        this.toast.deleted('Precio');
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al eliminar';
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar' });
      }
    });
  }

  /** Prefijo/símbolo de la moneda seleccionada para resolver (ej. 'S/'). */
  get resolveSymbol(): string | undefined {
    return this.currencies.find(c => c.id === this.resolveCurrencyId)?.prefix;
  }

  /** Código ISO de esa misma moneda, para saber cuántos decimales lleva. */
  get resolveCode(): string | undefined {
    return this.currencies.find(c => c.id === this.resolveCurrencyId)?.code;
  }

  /** Al cambiar la moneda a resolver, se limpia el resultado/aviso previo (estado "aún no resuelto"). */
  onResolveCurrencyChange(): void {
    this.resolveResult = null;
    this.resolveError = null;
  }

  resolver(): void {
    if (!this.resolveCurrencyId) return;
    this.resolving = true;
    this.resolveResult = null;
    this.resolveError = null;
    this.priceCurrencyApi.resolveProductPriceCurrency(this.productId, this.resolveCurrencyId).subscribe({
      next: (data) => {
        this.resolveResult = data;
        this.resolving = false;
      },
      error: (err) => {
        this.resolving = false;
        this.resolveResult = null;
        // 400 = la moneda no tiene tipo de cambio configurado. El backend ya nombra la
        // moneda concreta y remite a la pantalla de Tipos de Cambio (Incidente #37);
        // se muestra su mensaje tal cual junto a un enlace directo a esa pantalla.
        // Otros errores (p. ej. 404 = sin precio configurado para esa moneda) no muestran este aviso.
        this.resolveError = err?.status === 400
          ? (err.error?.message || 'Esta moneda no tiene tipo de cambio configurado')
          : null;
      }
    });
  }

  /**
   * Formatea un monto con el símbolo/prefijo de SU moneda (Incidente #37).
   * La conversión/asociación de moneda la resuelve el backend (resolvedPrice /
   * currencySymbol) o se toma el prefijo de la moneda destino; ya no se fuerza USD.
   */
  formatPrecio(price?: number, symbol?: string, currencyCode?: string): string {
    // Los decimales los pone la moneda (ISO 4217), no un 2 fijo: el yen no
    // tiene subunidad y el dinar kuwaití tiene tres decimales.
    return formatMoney(price, symbol, currencyCode);
  }
}
